import { PropsWithChildren, useEffect, useState } from "react";
import { useApiClient } from "../api/useApiClient";
import { NavLink, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { useRoles } from "../auth/useRoles";
import {
  ROLE_CONTADURIA,
  ROLE_COMPRADOR,
  ROLE_CONTROL_PAGOS,
  ROLE_APROBADOR_PAGOS,
  roleLabel,
} from "../constants/roles";
import { EVENTO_LOTES_PAGO_CAMBIARON, cantidadParaAprobar } from "../utils/lotesPago";
import { useContadores } from "../contexts/ContadoresContext";
import { guardarMenuOculto, leerMenuOculto, type AlmacenSimple } from "../utils/menuPreferencia";

/** localStorage puede lanzar al solo acceder a él (sitio sin permiso de datos). */
function almacenLocal(): AlmacenSimple | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Título de la bandeja actual, para la barra superior del móvil. Al esconder el
 * menú se pierde la referencia de dónde estás (el `NavLink` activo), así que la
 * barra la repone.
 *
 * Se deriva de la ruta y no de un contexto nuevo: son 5 rutas fijas y el
 * `pathname` ya es la fuente de verdad.
 */
function tituloDeRuta(pathname: string): string {
  if (pathname.startsWith("/contaduria")) return "Para autorizar";
  if (pathname.startsWith("/rechazadas")) return "Rechazadas";
  if (pathname.startsWith("/archivadas")) return "Archivadas";
  if (pathname.startsWith("/buscar")) return "Buscar factura";
  if (pathname.startsWith("/posteo")) return "Posteo a SAP";
  if (pathname.startsWith("/fuera-de-carril-a")) return "Fuera de Carril A";
  if (pathname.startsWith("/facturas-para-pagar")) return "Control de pagos";
  if (pathname.startsWith("/aprobacion-pagos")) return "Aprobación de pagos";
  if (pathname.startsWith("/comprador")) return "Aprobación";
  return "Bandeja de Facturas";
}

/**
 * Badge con la cantidad de filas de la bandeja. [NUEVO 08/08/2026]
 * No se renderiza si el contador no está disponible (backend frío o sin
 * permiso) — mostrar "0" cuando en realidad no se sabe sería peor que no
 * mostrar nada. El 0 real sí se muestra: es información útil ("no hay nada
 * pendiente").
 */
function BadgeConteo({ cantidad }: { cantidad: number | undefined }) {
  if (cantidad === undefined) return null;
  return (
    <span className={`nav-badge${cantidad === 0 ? " nav-badge-vacio" : ""}`}>{cantidad}</span>
  );
}

export function Layout({ children }: PropsWithChildren) {
  const { instance, accounts } = useMsal();
  const roles = useRoles();
  const { contadores } = useContadores();
  const account = accounts[0];

  /**
   * [NUEVO 11/08/2026] Menú lateral colapsable en pantallas chicas.
   *
   * En un celular la barra de 230px fijos se comía ~60% del ancho y la tabla
   * quedaba con ~160px: los encabezados se apilaban unos sobre otros. Detectado
   * por Liber Almeida probando en un móvil.
   *
   * Solo afecta a pantallas angostas: de 900px para arriba el CSS ignora este
   * estado por completo y la barra se ve fija como siempre — o sea el escritorio
   * no cambia en nada.
   */
  const [menuAbierto, setMenuAbierto] = useState(false);

  /**
   * [NUEVO 20/10/2026] Ocultar el menú también en escritorio, para ganar ancho
   * en tablas como Control de pagos (12 columnas). Es independiente de
   * `menuAbierto` (el cajón del celular) y solo tiene efecto de 901px para
   * arriba. Por defecto visible; la elección se recuerda en el navegador.
   */
  const [menuOculto, setMenuOculto] = useState(() => leerMenuOculto(almacenLocal()));
  function alternarMenuEscritorio() {
    setMenuOculto((oculto) => {
      guardarMenuOculto(almacenLocal(), !oculto);
      return !oculto;
    });
  }
  const location = useLocation();

  /**
   * [NUEVO 20/10/2026] Funcionalidades detrás de un flag del backend (hoy solo
   * el buscador). Ante cualquier error queda apagado: un link que lleva a un
   * 404 es peor que no mostrarlo.
   */
  const api = useApiClient();
  const [buscadorHabilitado, setBuscadorHabilitado] = useState(false);
  const puedeBuscar = roles.includes(ROLE_CONTADURIA) || roles.includes(ROLE_COMPRADOR);
  // [NUEVO 04/11/2026] Lotes de pago: link "Aprobación de pagos" detrás del
  // flag `lotes_pago`, para Aprobadores de pagos y Contaduría.
  const esAprobadorPagos = roles.includes(ROLE_APROBADOR_PAGOS);
  const puedeVerAprobacionPagos = esAprobadorPagos || roles.includes(ROLE_CONTADURIA);
  const [lotesPagoHabilitado, setLotesPagoHabilitado] = useState(false);
  useEffect(() => {
    if (!puedeBuscar && !puedeVerAprobacionPagos) return;
    let cancelado = false;
    api
      .obtenerFuncionalidades()
      .then((f) => {
        if (cancelado) return;
        setBuscadorHabilitado(Boolean(f.buscador));
        setLotesPagoHabilitado(Boolean(f.lotes_pago));
      })
      .catch(() => undefined);
    return () => {
      cancelado = true;
    };
  }, [api, puedeBuscar, puedeVerAprobacionPagos]);

  // Badge "Para aprobar": solo para quien aprueba. Si falla, no se muestra.
  // La página avisa con un evento de ventana cuando aprueba o rechaza.
  const [lotesPorAprobar, setLotesPorAprobar] = useState<number | undefined>(undefined);
  // Solo cuenta lo que esta persona puede aprobar (no lo que armó ni lo que ya aprobó).
  const usuarioActual = account?.username ?? "";
  useEffect(() => {
    if (!lotesPagoHabilitado || !esAprobadorPagos) {
      setLotesPorAprobar(undefined);
      return;
    }
    let cancelado = false;
    let pedido = 0;
    const contar = () => {
      const n = ++pedido;
      api
        .listarLotesPago({ estado: "CONCILIADO" })
        .then((lotes) => {
          if (!cancelado && n === pedido) setLotesPorAprobar(cantidadParaAprobar(lotes, usuarioActual));
        })
        .catch(() => {
          if (!cancelado && n === pedido) setLotesPorAprobar(undefined);
        });
    };
    contar();
    window.addEventListener(EVENTO_LOTES_PAGO_CAMBIARON, contar);
    return () => {
      cancelado = true;
      window.removeEventListener(EVENTO_LOTES_PAGO_CAMBIARON, contar);
    };
  }, [api, lotesPagoHabilitado, esAprobadorPagos, usuarioActual]);

  // Cerrar al navegar: en un celular el menú tapa el contenido, así que dejarlo
  // abierto después de elegir una bandeja obligaría a un toque extra.
  const cerrarMenu = () => setMenuAbierto(false);

  function logout() {
    instance.logoutRedirect();
  }

  return (
    <div className={`app-shell${menuOculto ? " app-shell-menu-oculto" : ""}`}>
      {/* Botón para volver a mostrar el menú: solo existe en escritorio y solo
          mientras está oculto (ver el @media de min-width en el CSS). */}
      {menuOculto && (
        <button
          type="button"
          className="menu-mostrar-escritorio"
          aria-label="Mostrar menú"
          title="Mostrar menú"
          onClick={alternarMenuEscritorio}
        >
          ☰
        </button>
      )}

      {/* Barra superior: solo visible en pantallas chicas (ver el @media del
          CSS). Muestra en qué bandeja estás, que es lo que se pierde al
          esconder el menú. */}
      <header className="topbar">
        <button
          className="topbar-toggle"
          aria-label={menuAbierto ? "Cerrar menú" : "Abrir menú"}
          aria-expanded={menuAbierto}
          onClick={() => setMenuAbierto((abierto) => !abierto)}
        >
          {menuAbierto ? "✕" : "☰"}
        </button>
        <span className="topbar-titulo">{tituloDeRuta(location.pathname)}</span>
      </header>

      {/* Fondo oscuro para cerrar tocando afuera. Solo se renderiza cuando el
          menú está abierto, así no intercepta clics en escritorio. */}
      {menuAbierto && <div className="backdrop" onClick={cerrarMenu} />}

      <aside className={`sidebar${menuAbierto ? " sidebar-abierta" : ""}`} onClick={cerrarMenu}>
        <div className="brand">
          {/* stopPropagation: el <aside> cierra el cajón del celular al hacer
              clic en cualquier parte; acá no corresponde. */}
          <button
            type="button"
            className="menu-ocultar-escritorio"
            aria-label="Ocultar menú"
            title="Ocultar menú"
            onClick={(e) => {
              e.stopPropagation();
              alternarMenuEscritorio();
            }}
          >
            «
          </button>
          Pontyn S.A.
          <b>Bandeja de Facturas</b>
        </div>

        <nav className="nav">
          {puedeBuscar && buscadorHabilitado && (
            <NavLink to="/buscar" className="nav-link">
              🔍 Buscar factura
            </NavLink>
          )}
          {roles.includes(ROLE_CONTADURIA) && (
            <>
              <NavLink to="/contaduria" className="nav-link">
                Para autorizar
                <BadgeConteo cantidad={contadores?.pendiente_contaduria} />
              </NavLink>
              <NavLink to="/rechazadas" className="nav-link">
                Rechazadas
                <BadgeConteo cantidad={contadores?.rechazadas} />
              </NavLink>
              {/* [NUEVO 20/10/2026] Solo lectura. Sin badge: es un archivo,
                  no algo pendiente de hacer. */}
              <NavLink to="/archivadas" className="nav-link">
                Archivadas
              </NavLink>
              <NavLink to="/posteo" className="nav-link">
                Posteo a SAP
                {/* El badge suma los TRES estados que muestra la pantalla
                    (en cola + en SAP + error) para que coincida con lo que se
                    ve al entrar — mismo criterio de siempre. */}
                <BadgeConteo
                  cantidad={
                    contadores === null
                      ? undefined
                      : contadores.aprobado_pendiente_posteo +
                        contadores.en_sap +
                        contadores.error_posteo
                  }
                />
              </NavLink>
              <NavLink to="/fuera-de-carril-a" className="nav-link">
                Fuera de Carril A
                <BadgeConteo cantidad={contadores?.fuera_de_carril_a} />
              </NavLink>
            </>
          )}
          {/* [AMPLIADO 11/08/2026] Contaduría también ve este link, en modo
              seguimiento (ver CompradorPage): el backend ya le devolvía la
              lista completa, pero sin el link no tenía forma de llegar. Lo
              detectó Micaela Morales (Contaduría) al no encontrar la página.
              El badge para Contaduría es el total global; para quien es solo
              Aprobador, el backend ya lo acota a lo asignado a él. */}
          {(roles.includes(ROLE_COMPRADOR) || roles.includes(ROLE_CONTADURIA)) && (
            <NavLink to="/comprador" className="nav-link">
              {roles.includes(ROLE_COMPRADOR) ? "Para aprobar" : "Pendientes del Aprobador"}
              <BadgeConteo cantidad={contadores?.pendiente_comprador} />
            </NavLink>
          )}

          {/* [NUEVO 01/09/2026] Bandeja de control de pagos. La ven el rol
              nuevo y Contaduria, que supervisa todas las bandejas. El badge
              cuenta las HABILITADAS para pagar, no el total: es lo accionable
              de la semana. */}
          {(roles.includes(ROLE_CONTROL_PAGOS) || roles.includes(ROLE_CONTADURIA)) && (
            <NavLink to="/facturas-para-pagar" className="nav-link">
              Control de pagos
              <BadgeConteo cantidad={contadores?.facturas_para_pagar} />
            </NavLink>
          )}

          {/* [NUEVO 04/11/2026] Aprobación de lotes de pago, detrás del flag.
              El badge cuenta los "Para aprobar" y solo lo ve quien aprueba. */}
          {puedeVerAprobacionPagos && lotesPagoHabilitado ? (
            <NavLink to="/aprobacion-pagos" className="nav-link">
              Aprobación de pagos
              <BadgeConteo cantidad={esAprobadorPagos ? lotesPorAprobar : undefined} />
            </NavLink>
          ) : null}
        </nav>

        <div className="whoami">
          <b>{account?.name ?? account?.username}</b>
          <span>{roles.map(roleLabel).join(" · ") || "Sin rol asignado"}</span>
          <button className="btn-ghost" onClick={logout}>
            Cerrar sesión
          </button>
        </div>
      </aside>

      <main className="main">{children}</main>
    </div>
  );
}
